// src/Pages/LoginPage/LoginPage.jsx

import React, { useState, useEffect } from 'react';
import './LoginPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpotify, faApple, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { faComment } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginWithToken, setGuestLogin, restoreAuthState } from '../../store/slices/authSlice';
import axios from 'axios';
import NotificationPopup from '../../Components/NotificationPopup/NotificationPopup';
import { Capacitor } from '@capacitor/core';
import LoadingSpinner from '../../Components/LoadingSpinner/LoadingSpinner';

// 소셜 로그인 헬퍼
import {
  signInWithGoogle,
  signInWithKakao,
  signInWithApple,
  signInWithSpotify
} from '../../services/authService';

// ← NEW: helper to exchange your FastAPI JWT for a Firebase Custom Token (and set displayName)
import { signInWithBackendToken } from '../../firebase';

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error: authErrorFromStore } = useSelector((state) => state.auth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [notificationText, setNotificationText] = useState("");
  const [showFullSpinner, setShowFullSpinner] = useState(false);

  // Determine what error message to display, if any
  let displayErrorMessage = null;
  if (formError) {
    displayErrorMessage = formError; // Login attempt error takes precedence
  } else if (authErrorFromStore && authErrorFromStore !== 'No auth data found') {
    // Display auth store error only if it's not the "No auth data found" message from restoreAuthState
    displayErrorMessage = typeof authErrorFromStore === 'string' ? authErrorFromStore : (authErrorFromStore.detail || '알 수 없는 오류가 발생했습니다.');
  }

  // (1) restore from storage if possible
  useEffect(() => {
    dispatch(restoreAuthState());
  }, [dispatch]);

  // show any ?error=… from redirects
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errMessage = params.get('error');
    if (errMessage) {
      setNotificationText(decodeURIComponent(errMessage));
      params.delete('error');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleLogin = async (service) => {
    const isNative = Capacitor.isNativePlatform();
    try {
      if (service === 'google') {
        await signInWithGoogle();
      } else if (service === 'kakao') {
        await signInWithKakao();
      } else if (service === 'spotify') {
        await signInWithSpotify();
      } else if (service === 'apple') {
        const appleAuthData = await signInWithApple();
        
        if (isNative && appleAuthData?.access_token && appleAuthData?.user) {
    
          const { access_token, user, refresh_token } = appleAuthData;
          
          const { user: loggedInUser, token: confirmedAccessToken } = await dispatch(loginWithToken({
            token: access_token,
            userFromCallback: user,
            refreshTokenForNative: refresh_token 
          })).unwrap();

          await signInWithBackendToken(confirmedAccessToken, loggedInUser.name);
  
          setShowFullSpinner(true);
          navigate('/home');

        } else if (!isNative && service === 'apple') {
  
        } else if (isNative && (!appleAuthData?.access_token || !appleAuthData?.user)) {
          
          setNotificationText('Apple 로그인에 실패했습니다. 다시 시도해주세요.');
        }
        return;
      } else {
        if (!isNative && (service === 'google' || service === 'kakao' || service === 'spotify')){
  
        }
      }
    } catch (err) {
      console.error(`Social login error (${service}):`, err);
      setNotificationText(`소셜 로그인 중 오류가 발생했습니다: ${err.message || '알 수 없는 오류'}`);
    }
  };

  const handleLaterClick = () => {
    dispatch(setGuestLogin({
      id: 'guest',
      name: 'Guest',
      email: 'guest@example.com',
      isGuest: true,
    }));
    setShowFullSpinner(true);
    navigate('/home');
  };

  const handleSignupClick = () => navigate('/signup');
  const handleFindPw = () => navigate('/find-pw');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    try {
      // 1) FastAPI login
      const resp = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        { email, password },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const { access_token } = resp.data;

      // 2) Store JWT + fetch user
      const { user, token } = await dispatch(loginWithToken({ token: access_token })).unwrap();

      // 3) Sign into Firebase + set displayName
      await signInWithBackendToken(token, user.name);

      // 4) Done → home
      setShowFullSpinner(true);
      navigate('/home');
    } catch (err) {
      console.error('Login error:', err);
      const detail = err.response?.data?.detail;
      setFormError(detail || '로그인 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <div className="login-page">
      {showFullSpinner && <LoadingSpinner />}
      {notificationText && (
        <NotificationPopup
          message={notificationText}
          onClose={() => setNotificationText("")}
        />
      )}

      <div className="logo-container">
        <h1 className="logo-text">H2P</h1>
        <h2 className="logo-subtitle">HOW2PLAY</h2>
      </div>

      <p className="login-subtitle">당신의 취향에 맞는 클럽을 찾아가세요!</p>

      <form onSubmit={handleSubmit}>
        <input
          type="email"
          placeholder="이메일"
          className="input-field"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          type="password"
          placeholder="비밀번호"
          className="input-field"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {displayErrorMessage && (
          <p className="error-message">{displayErrorMessage}</p>
        )}
        <button className="login-submit-btn" type="submit" disabled={isLoading}>
          {isLoading ? (
            <div className="loading-spinner-container">
              <img
                src="/How2Play_mainLogo128.png"
                alt="Loading"
                className="logo-spin"
              />
            </div>
          ) : (
            '로그인'
          )}
        </button>
      </form>

      <div className="find-info">
        <span onClick={handleSignupClick} className="find-id-link">
          회원가입
        </span>{' '}
        |{' '}
        <span onClick={handleFindPw} className="find-id-link">
          비밀번호 찾기
        </span>
      </div>

      <div className="login-buttons">
        <button className="login-btn google" onClick={() => handleLogin('google')}>
          <FontAwesomeIcon icon={faGoogle} className="icon" /> Google 계속하기
        </button>
        <button className="login-btn kakao" onClick={() => handleLogin('kakao')}>
          <FontAwesomeIcon icon={faComment} className="icon" /> 카카오 계속하기
        </button>
        <button className="login-btn spotify" onClick={() => handleLogin('spotify')}>
          <FontAwesomeIcon icon={faSpotify} className="icon" /> Spotify 계속하기
        </button>
        <button className="login-btn apple" onClick={() => handleLogin('apple')}>
          <FontAwesomeIcon icon={faApple} className="icon" /> Apple 계속하기
        </button>
      </div>

      <div className="later-link">
        <span onClick={handleLaterClick}>나중에 하기</span>
      </div>
    </div>
  );
};

export default LoginPage;
